import React, { useState } from 'react';
import clsx from 'clsx';
import axios from 'axios';
import { enqueueSnackbar } from 'notistack';
import { ChatConfig, Message } from './SidebarChat';
import { KeenIcon } from '@/components';
import { formatDate } from '@/pages/buddy/utils/Date.tsx';
import { LoaderType, Loading } from '@/layouts/buddy/loading';
import { CPSModal } from "@/layouts/buddy/cps/CPSModal.tsx";
import {TagEnum} from "@/pages/buddy/types/CPSPromptData.ts";

interface Props {
    height?: number;
    config: ChatConfig;
    messages: Message[];
    setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
    onUpdateComplete?: () => void;
}

const SidebarChatContent = ({ height = 0, config, messages, setMessages, onUpdateComplete }: Props) => {
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    const API_URL = import.meta.env.VITE_APP_API_URL;

    const handleSend = async () => {
        if (!input.trim() || !config.entityId || !config.entityType) return;

        setLoading(true);

        try {
            const token = localStorage.getItem('authToken');
            const url = `${API_URL}/${config.entityType}/update-cps/${config.entityId}`;

            const response = await axios.post(
                url,
                { text: input },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            const now = new Date().toISOString();

            const userMessage: Message = {
                role: 'user',
                content: input,
                timestamp: now,
            };
            const aiMessage: Message = {
                role: 'ai',
                content: response.data?.reply || 'Job updated.',
                timestamp: now,
            };

            setMessages(prev =>
                [aiMessage, userMessage, ...prev].sort(
                    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
                )
            );

            if (onUpdateComplete) onUpdateComplete();
        } catch (err: any) {
            enqueueSnackbar(`Failed to fetch response: ${err?.message || err}`, {
                variant: 'error',
            });
        } finally {
            setInput('');
            setLoading(false);
        }
    };

    const renderIcon = (role: 'user' | 'ai') => {
        return role === 'user' ? (
            <KeenIcon icon="user" className="text-l" />
        ) : (
            <KeenIcon icon="technology-4" className="text-l" />
        );
    };

    const firstUserIdx = messages.findIndex(m => m.role === 'user');

    return (
        <div className="flex flex-col h-full overflow-hidden gap-4 px-2">
            <div className="border-b bg-base-200">
                <div className="flex items-center justify-center w-full mb-6">
                    {loading ? (
                        <Loading
                            loadingText="Processing Instructions"
                            loaderType={LoaderType.LOADING_FILES}
                            loading={loading}
                            delay={0}
                        />
                    ) : (
                        <div className="flex flex-col gap-6 w-full">
                            <textarea
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) =>
                                    e.key === 'Enter' && !e.shiftKey && handleSend()
                                }
                                placeholder="Type your instructions for Buddy here."
                                className="textarea textarea-bordered w-full min-h-[4.5rem]"
                                rows={4}
                            />
                            <div className="flex gap-2">
                                <button
                                    onClick={handleSend}
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    disabled={loading}
                                >
                                    <KeenIcon icon="plus" /> Send Instructions
                                </button>
                                <button
                                    onClick={() => setModalOpen(true)}
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                >
                                    <KeenIcon icon="magnifier" /> Search Prompt
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {messages.length > 0 && (
                <>
                    <h2 className="text-lg font-semibold text-gray-900 leading-tight">Instruction History</h2>
                    <div className="flex-1 min-h-0 overflow-y-auto space-y-6 pr-2 flex flex-col">
                        {(() => {
                            const pairs = [];
                            for (let i = 0; i < messages.length; i++) {
                                const userMsg = messages[i];
                                const aiMsg = messages[i + 1];

                                if (userMsg.role !== 'user' || !aiMsg || aiMsg.role !== 'ai') continue;

                                pairs.push(
                                    <div key={i} className="flex flex-col gap-y-2">
                                        <div className="flex flex-col items-end">
                                            <div
                                                className="text-sm px-4 py-2 rounded-xl max-w-xs bg-secondary text-gray-700 text-right whitespace-pre-wrap">
                                                {userMsg.isLoading ? (
                                                    <span className="animate-pulse">...</span>
                                                ) : (
                                                    userMsg.content
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex justify-center text-gray-400">
                                            <KeenIcon icon="arrow-down" className="w-4 h-4" />
                                        </div>

                                        <div className="flex flex-col items-start">
                                            <div className={clsx(
                                                'text-sm px-4 py-2 rounded-xl max-w-xs whitespace-pre-wrap',
                                                i <= firstUserIdx + 1
                                                    ? 'bg-primary text-white'
                                                    : 'bg-[rgb(247,169,75)] text-white'
                                            )}>
                                                {aiMsg.isLoading ? (
                                                    <span className="animate-pulse">...</span>
                                                ) : (
                                                    aiMsg.content
                                                )}
                                            </div>
                                            <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                                                {renderIcon(aiMsg.role)}
                                                <span>{formatDate(aiMsg.timestamp)}</span>
                                            </div>
                                        </div>
                                    </div>
                                );

                                i++; // skip next msg (already included)
                            }
                            return pairs;
                        })()}
                    </div>
                </>
            )}

            {/* CPSModal */}
            <CPSModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                defaultTags={[config.entityType as TagEnum]}
                onInsertText={text => {
                    setModalOpen(false);
                    setInput(text);
                }}
            />
        </div>
    );
};

export { SidebarChatContent };
